"use client";

import Link from "next/link";
import { CheckCircle2, ArrowRight } from "lucide-react";

interface PhotoRequestAlreadySentBlockProps {
	/** Optional property address to show in "Requested Property" card */
	address?: string;
}

/**
 * In-place UI shown when the user has already submitted a photo request for this property.
 * Matches the success screen layout (checkmark, headline, "What Happens Next?", Profile link).
 */
export function PhotoRequestAlreadySentBlock({
	address,
}: PhotoRequestAlreadySentBlockProps) {
	return (
		<div className="py-8 px-4 text-center">
			<div className="inline-flex items-center justify-center rounded-full p-5 mb-6 bg-gradient-to-br from-[#16a34a] to-[#15803d]">
				<CheckCircle2 className="w-12 h-12 text-white" />
			</div>

			<div>
				<h2 className="text-[28px] font-semibold text-[#112133] mb-3">
					Request Already Submitted
				</h2>
				<p className="text-base text-[#6b7280] leading-relaxed mb-8 max-w-lg mx-auto">
					You&apos;ve already requested photos for this property. Our
					team will deliver high-quality images within{" "}
					<strong className="text-[#16251c]">24-48 hours</strong>.
				</p>
			</div>

			{address && (
				<div className="bg-gradient-to-br from-[#f8f9fa] to-[#f3f4f6] rounded-xl p-6 border border-[#e5e7eb] mb-8 text-left max-w-lg mx-auto">
					<p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-2">
						Requested Property
					</p>
					<p className="text-base font-semibold text-[#112133]">
						{address}
					</p>
				</div>
			)}

			<div className="bg-[#A7FF5E]/10 border border-[#A7FF5E]/30 rounded-xl p-6 text-left max-w-lg mx-auto">
				<p className="text-base font-semibold text-[#16251c] mb-4">
					What Happens Next?
				</p>
				<div className="space-y-3">
					<div className="flex items-start gap-3">
						<div className="bg-[#16251c] text-[#A7FF5E] rounded-full w-6 h-6 flex items-center justify-center font-semibold text-xs shrink-0 mt-0.5">
							1
						</div>
						<p className="text-sm text-[#16251c]/80">
							We&apos;ll schedule a visit to the property location
						</p>
					</div>
					<div className="flex items-start gap-3">
						<div className="bg-[#16251c] text-[#A7FF5E] rounded-full w-6 h-6 flex items-center justify-center font-semibold text-xs shrink-0 mt-0.5">
							2
						</div>
						<p className="text-sm text-[#16251c]/80">
							Our photographer will capture professional images
						</p>
					</div>
					<div className="flex items-start gap-3">
						<div className="bg-[#16251c] text-[#A7FF5E] rounded-full w-6 h-6 flex items-center justify-center font-semibold text-xs shrink-0 mt-0.5">
							3
						</div>
						<p className="text-sm text-[#16251c]/80">
							Photos will be processed and uploaded to your
							account
						</p>
					</div>
					<div className="flex items-start gap-3">
						<div className="bg-[#16251c] text-[#A7FF5E] rounded-full w-6 h-6 flex items-center justify-center font-semibold text-xs shrink-0 mt-0.5">
							4
						</div>
						<p className="text-sm text-[#16251c]/80">
							You&apos;ll receive a notification when they&apos;re
							ready
						</p>
					</div>
				</div>
			</div>

			<div className="mt-6 text-center">
				<Link
					href="/profile"
					className="inline-flex items-center gap-2 text-sm font-semibold text-[#16251c] hover:text-[#417714] transition-colors group"
				>
					<span>Manage your requests under Profile</span>
					<ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
				</Link>
			</div>
		</div>
	);
}
